import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Printer, Ban, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { formatDOP, formatDate } from "@/lib/format";
import { useConfirm } from "@/components/ConfirmDialog";

const METHODS = ["efectivo", "tarjeta", "transferencia", "cheque"];

function ReceiptBuilder({ open, onOpenChange, kind, onCreated }) {
  const confirm = useConfirm();
  const isIncome = kind === "income";
  const [counterparts, setCounterparts] = useState([]);
  const [counterpartId, setCounterpartId] = useState("");
  const [openDocs, setOpenDocs] = useState([]);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("efectivo");
  const [notes, setNotes] = useState("");
  const [expenseCategory, setExpenseCategory] = useState("");
  const [isAdvance, setIsAdvance] = useState(false);
  const [beneficiary, setBeneficiary] = useState("");
  // Distribución: map de doc_id → monto aplicado
  const [distribution, setDistribution] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(isIncome ? "/customers" : "/suppliers").then(({ data }) => setCounterparts(data || []));
    setCounterpartId(""); setOpenDocs([]); setDistribution({});
    setAmount(""); setMethod("efectivo"); setNotes(""); setExpenseCategory(""); setIsAdvance(false); setBeneficiary("");
  }, [open, isIncome]);

  useEffect(() => {
    if (!counterpartId) { setOpenDocs([]); setDistribution({}); return; }
    const url = isIncome
      ? `/customers/${counterpartId}/open-invoices`
      : `/suppliers/${counterpartId}/open-purchases`;
    api.get(url).then(({ data }) => setOpenDocs(data || [])).catch(() => setOpenDocs([]));
    setDistribution({});
  }, [counterpartId, isIncome]);

  const totalApplied = useMemo(
    () => Object.values(distribution).reduce((s, v) => s + (Number(v) || 0), 0),
    [distribution]
  );
  const amountNum = Number(amount) || 0;
  const remaining = amountNum - totalApplied;

  const fifoFill = () => {
    let left = amountNum;
    const next = {};
    for (const d of openDocs) {
      if (left <= 0.005) break;
      const bal = Number(d.balance_due) || 0;
      const apply = Math.min(left, bal);
      if (apply > 0) next[d.id] = Number(apply.toFixed(2));
      left -= apply;
    }
    setDistribution(next);
  };

  const clearDist = () => setDistribution({});

  const submit = async () => {
    if (!amountNum || amountNum <= 0) return toast.error("Monto inválido");
    if (isIncome && !counterpartId) return toast.error("Selecciona un cliente");
    const applications = Object.entries(distribution)
      .filter(([, v]) => Number(v) > 0)
      .map(([doc_id, v]) => ({ doc_id, amount: Number(v) }));
    if (applications.length > 0 && Math.abs(totalApplied - amountNum) > 0.01) {
      return toast.error(`El total aplicado (${totalApplied.toFixed(2)}) debe coincidir con el monto (${amountNum.toFixed(2)})`);
    }
    if (!isIncome && !isAdvance && applications.length === 0 && counterpartId) {
      const ok = await confirm({
        title: "¿Registrar como gasto?",
        description: "Este egreso no se aplica a ninguna compra y se registrará como GASTO. Si es un adelanto de un trabajo cuyo E41/E43 harás después, cancela y marca 'Es un anticipo' para no contar el gasto dos veces.",
        confirmLabel: "Registrar como gasto",
      });
      if (!ok) return;
    }
    if (isAdvance && !counterpartId && !beneficiary.trim()) return toast.error("Indica a quién se le da el anticipo");
    setSaving(true);
    try {
      const payload = {
        kind,
        counterpart_id: counterpartId || null,
        counterpart_name: counterparts.find((c) => c.id === counterpartId)?.name || beneficiary.trim(),
        amount: amountNum,
        payment_method: method,
        notes,
        applications,
      };
      if (!isIncome) { payload.expense_category = isAdvance ? "" : expenseCategory; payload.is_advance = isAdvance; }
      const { data } = await api.post("/receipts", payload);
      toast.success(`Recibo ${data.number} creado`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isIncome ? <TrendingUp className="w-4 h-4 text-emerald-600" /> : <TrendingDown className="w-4 h-4 text-orange-600" />}
            Nuevo recibo de {isIncome ? "ingreso" : "egreso"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="overline">{isIncome ? "Cliente" : "Suplidor"} {isIncome && <span className="text-destructive">*</span>}</Label>
            <Select value={counterpartId} onValueChange={setCounterpartId}>
              <SelectTrigger className="mt-1" data-testid="receipt-counterpart">
                <SelectValue placeholder="Selecciona" />
              </SelectTrigger>
              <SelectContent>
                {counterparts.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}{c.rnc_cedula ? ` · ${c.rnc_cedula}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline">Monto <span className="text-destructive">*</span></Label>
            <Input type="number" step="0.01" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 font-mono" data-testid="receipt-amount" />
          </div>
          <div>
            <Label className="overline">Método de pago</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {!isIncome && (
            <div>
              <Label className="overline">Categoría de gasto (opcional)</Label>
              <Input value={expenseCategory} onChange={(e) => setExpenseCategory(e.target.value)} className="mt-1" placeholder="Ej: renta, servicios, otros…" disabled={isAdvance} />
            </div>
          )}
          {!isIncome && (
            <div className="col-span-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm space-y-2" data-testid="receipt-advance-box">
              <label className="flex items-center gap-3">
                <Switch checked={isAdvance} onCheckedChange={(v) => { setIsAdvance(v); if (v) setDistribution({}); }} data-testid="receipt-is-advance" />
                <span><b>Es un anticipo</b> de un trabajo o compra cuyo E41/E43 se registrará después (no se cuenta como gasto todavía)</span>
              </label>
              {isAdvance && !counterpartId && (
                <Input value={beneficiary} onChange={(e) => setBeneficiary(e.target.value)} placeholder="Beneficiario (si no es un suplidor registrado, ej. empleado para un E43)" data-testid="receipt-beneficiary" />
              )}
            </div>
          )}
        </div>

        {counterpartId && !isAdvance && (
          <div className="mt-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="overline">
                {isIncome ? "Aplicar a facturas del cliente" : "Aplicar a compras del suplidor"}
              </Label>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={fifoFill} disabled={!amountNum || openDocs.length === 0} data-testid="receipt-fifo">
                  Distribuir FIFO
                </Button>
                <Button size="sm" variant="ghost" onClick={clearDist}>Limpiar</Button>
              </div>
            </div>
            <div className="border border-border rounded-md max-h-[260px] overflow-y-auto">
              {openDocs.length === 0 ? (
                <div className="p-4 text-sm text-center text-muted-foreground">
                  {isIncome ? "El cliente no tiene facturas con saldo pendiente." : "El suplidor no tiene compras con saldo pendiente."}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="overline border-b border-border">
                    <tr>
                      <th className="text-left p-2">Doc</th>
                      <th className="text-right p-2">Total</th>
                      <th className="text-right p-2">Saldo</th>
                      <th className="text-right p-2 w-28">Aplicar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openDocs.map((d) => (
                      <tr key={d.id} className="border-b border-border/40 hover:bg-accent">
                        <td className="p-2 data-cell">{d.number}</td>
                        <td className="p-2 text-right data-cell">{formatDOP(d.total)}</td>
                        <td className="p-2 text-right data-cell text-orange-700">{formatDOP(d.balance_due)}</td>
                        <td className="p-2">
                          <Input
                            type="number" step="0.01" min={0} max={d.balance_due}
                            value={distribution[d.id] ?? ""}
                            onChange={(e) => setDistribution((s) => ({ ...s, [d.id]: e.target.value }))}
                            className="h-8 text-right font-mono"
                            data-testid={`apply-${d.number}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="mt-2 text-sm flex justify-between">
              <span className="text-muted-foreground">Aplicado: <span className="data-cell font-bold">{formatDOP(totalApplied)}</span></span>
              <span className={remaining < -0.01 ? "text-destructive font-bold" : "text-muted-foreground"}>
                {remaining > 0.01 ? `Falta: ${formatDOP(remaining)}` : remaining < -0.01 ? `Excede en: ${formatDOP(-remaining)}` : "OK"}
              </span>
            </div>
          </div>
        )}

        <div className="mt-3">
          <Label className="overline">Notas</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" rows={2} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={submit}
            disabled={saving || !amountNum || (isIncome && !counterpartId) || Math.abs(remaining) > 0.01 && totalApplied > 0}
            className="bg-primary font-bold"
            data-testid="receipt-save"
          >
            {saving ? "Guardando…" : "Guardar recibo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReceiptsList({ kind }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const confirm = useConfirm();

  const load = async () => {
    try {
      const { data } = await api.get(`/receipts?kind=${kind}`);
      setItems(data || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, [kind]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    if (!s) return items;
    return items.filter((r) =>
      (r.number || "").toLowerCase().includes(s) ||
      (r.counterpart_name || "").toLowerCase().includes(s) ||
      (r.notes || "").toLowerCase().includes(s)
    );
  }, [items, search]);

  const download = async (r) => {
    try {
      const res = await api.get(`/receipts/${r.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${r.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const voidR = async (r) => {
    const ok = await confirm({
      title: `Anular recibo ${r.number}`,
      description: "Esta acción reversa las aplicaciones sobre los documentos afectados.",
      confirmLabel: "Anular",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.post(`/receipts/${r.id}/void`);
      toast.success("Recibo anulado");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <Input
          placeholder="Buscar por número, contraparte o notas…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
          data-testid={`receipts-${kind}-search`}
        />
        <Button onClick={() => setOpen(true)} className="bg-primary font-bold" data-testid={`receipts-${kind}-new`}>
          <Plus className="w-4 h-4 mr-1.5" /> Nuevo recibo
        </Button>
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border bg-muted/30">
            <tr>
              <th className="text-left p-3">Número</th>
              <th className="text-left p-3">Fecha</th>
              <th className="text-left p-3">{kind === "income" ? "Cliente" : "Suplidor"}</th>
              <th className="text-right p-3">Monto</th>
              <th className="text-center p-3">Método</th>
              <th className="text-center p-3">Estado</th>
              <th className="text-right p-3 w-32">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-border/60 hover:bg-accent">
                <td className="p-3 data-cell font-bold">{r.number}</td>
                <td className="p-3 text-xs">{formatDate(r.date)}</td>
                <td className="p-3 truncate max-w-[220px]">
                  {r.counterpart_name || "—"}
                  {r.is_advance && (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800" data-testid={`receipt-advance-badge-${r.number}`}>
                      ANTICIPO {r.voided ? "· anulado" : r.advance_balance > 0.005 ? `· pend. ${formatDOP(r.advance_balance)}` : "· aplicado"}
                    </span>
                  )}
                </td>
                <td className={`p-3 text-right data-cell font-semibold ${kind === "income" ? "text-emerald-700" : "text-orange-700"}`}>
                  {formatDOP(r.amount)}
                </td>
                <td className="p-3 text-center text-xs uppercase">{r.payment_method}</td>
                <td className="p-3 text-center text-xs">
                  {r.voided
                    ? <span className="px-2 py-0.5 rounded bg-muted text-muted-foreground line-through">ANULADO</span>
                    : <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-700">Válido</span>}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => download(r)} title="PDF" data-testid={`receipt-pdf-${r.number}`}>
                      <Printer className="w-3.5 h-3.5" />
                    </Button>
                    {!r.voided && (
                      <Button size="sm" variant="ghost" onClick={() => voidR(r)} title="Anular" data-testid={`receipt-void-${r.number}`}>
                        <Ban className="w-3.5 h-3.5 text-destructive" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Sin recibos.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ReceiptBuilder open={open} onOpenChange={setOpen} kind={kind} onCreated={load} />
    </>
  );
}

export default function Receipts() {
  return (
    <PageContainer>
      <PageHeader
        title="Recibos"
        subtitle="Recibos de ingreso (cobros a clientes) y egreso (pagos a suplidores o gastos)."
        testid="receipts-header"
      />
      <Tabs defaultValue="income">
        <TabsList>
          <TabsTrigger value="income" data-testid="tab-income">
            <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Ingresos
          </TabsTrigger>
          <TabsTrigger value="expense" data-testid="tab-expense">
            <TrendingDown className="w-3.5 h-3.5 mr-1.5" /> Egresos
          </TabsTrigger>
        </TabsList>
        <TabsContent value="income" className="mt-4"><ReceiptsList kind="income" /></TabsContent>
        <TabsContent value="expense" className="mt-4"><ReceiptsList kind="expense" /></TabsContent>
      </Tabs>
    </PageContainer>
  );
}
